import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { LoadMoreButton as LoadingMoreButton } from '@sorare/core/src/atoms/buttons/LoadMoreButton';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';

import { Bid } from '../Bid';
import type {
  BidHistory_tokenAuction,
  BidHistory_tokenBidConnection,
} from './__generated__/index.graphql';

const Button = styled.div`
  align-self: center;
  padding-bottom: var(--double-unit);
`;

type Props = {
  loadMoreBids: (
    reload: boolean,
    variable: { bidCursor: string }
  ) => Promise<unknown>;
  bids: BidHistory_tokenBidConnection;
  auction?: BidHistory_tokenAuction;
  loading: boolean;
  skipFirst?: boolean;
  displayAbsoluteDate?: boolean;
};

export const BidHistory = ({
  bids,
  loading,
  auction,
  loadMoreBids,
  skipFirst = false,
  displayAbsoluteDate = false,
}: Props) => {
  const { hasNextPage, endCursor } = bids.pageInfo;

  const { bidToEarnRewards, bidToEarnRewardConfigs } = auction ?? {};

  if (bids.nodes.length === 0) return null;

  const bidRewardsMap = (bidToEarnRewards ?? []).reduce<
    Record<
      string,
      NonNullable<
        BidHistory_tokenAuction['bidToEarnRewards']
      >[number]['reward'][]
    >
  >((acc, bidToEarnReward) => {
    if (bidToEarnReward.bid) {
      acc[bidToEarnReward.bid.id] = acc[bidToEarnReward.bid.id]
        ? [...acc[bidToEarnReward.bid.id], bidToEarnReward.reward]
        : [bidToEarnReward.reward];
    }
    return acc;
  }, {});

  const list = skipFirst ? bids.nodes.slice(1) : bids.nodes;

  return (
    <Vertical gap={2}>
      <div>
        {list.map((bid, index) => (
          <Bid
            bid={bid}
            key={bid.id}
            displayAbsoluteDate={displayAbsoluteDate}
            bidRewards={bidRewardsMap[bid.id]}
            bidRewardConfigs={
              bidToEarnRewardConfigs?.find(x => x.bidderRank === index)?.rewards
            }
          />
        ))}
      </div>
      {!!hasNextPage && (
        <Button>
          <LoadingMoreButton
            hasMore={hasNextPage}
            loading={loading}
            loadMore={() => {
              loadMoreBids(false, { bidCursor: endCursor! });
            }}
          />
        </Button>
      )}
    </Vertical>
  );
};

BidHistory.fragments = {
  bid: gql`
    fragment BidHistory_tokenBidConnection on TokenBidConnection {
      nodes {
        id
        ...Bid_tokenBid
      }
      pageInfo {
        endCursor
        hasNextPage
      }
      totalCount
    }
    ${Bid.fragments.tokenBid}
  ` as TypedDocumentNode<BidHistory_tokenBidConnection>,
  tokenAuction: gql`
    fragment BidHistory_tokenAuction on TokenAuction {
      id
      bidToEarnRewardConfigs {
        bidderRank
        rewards {
          id
          ...Bid_AnyRewardConfigInterface
        }
      }
      bidToEarnRewards {
        id
        bid {
          id
        }
        reward {
          id
          ...Bid_AnyRewardInterface
        }
      }
    }

    ${Bid.fragments.AnyRewardInterface}
    ${Bid.fragments.AnyRewardConfigInterface}
  ` as TypedDocumentNode<BidHistory_tokenAuction>,
};
